import React, { useState } from 'react';
import { useWizard } from '../../context/WizardContext';
import { getAllPathways, getStepsForPathway } from '../../utils/dataCollectionData';
import { ClipboardList, Users, UserPlus, Database, Eye, FileText, X, ChevronRight, CheckCircle, Lightbulb, ArrowLeft, Square, CheckSquare } from 'lucide-react';

const iconMap = {
  ClipboardList: ClipboardList,
  Users: Users,
  UserPlus: UserPlus,
  Database: Database,
  Eye: Eye,
  FileText: FileText
};

const DataCollectionWizard = () => {
  const { dataA, dataDC, updateDataCollection, setDataCollectionComplete, switchWizard } = useWizard();
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [showStepModal, setShowStepModal] = useState(false);

  const pathways = getAllPathways();
  
  const completionConfirmed = dataDC?.completionConfirmed || false;

  const selectedTechniques = dataA?.techniques?.dataCollection || [];

  const techniqueToPathwayMap = {
    'Questionnaires': 'A',
    'Surveys': 'A',
    'Structured interviews': 'B',
    'Semi-structured interviews': 'B',
    'Unstructured interviews': 'B',
    'Focus groups': 'C',
    'Secondary data': 'D',
    'Document analysis': 'F',
    'Observation': 'E',
    'Participant observation': 'E'
  };

  const getRelevantPathways = () => {
    const relevantIds = new Set();
    selectedTechniques.forEach(technique => {
      const pathwayId = techniqueToPathwayMap[technique];
      if (pathwayId) {
        relevantIds.add(pathwayId);
      }
    });
    return relevantIds;
  };

  const relevantPathways = getRelevantPathways();

  const viewedSteps = dataDC?.viewedSteps || {};
  const selectedSteps = dataDC?.selectedSteps || {};

  const markStepViewed = (pathwayId, stepId) => {
    const key = `${pathwayId}_${stepId}`;
    if (!viewedSteps[key]) {
      updateDataCollection({
        viewedSteps: {
          ...viewedSteps,
          [key]: true
        }
      });
    }
  };

  const isStepViewed = (pathwayId, stepId) => {
    return viewedSteps[`${pathwayId}_${stepId}`] || false;
  };

  const isStepSelected = (pathwayId, stepId) => {
    return selectedSteps[`${pathwayId}_${stepId}`] || false;
  };

  const toggleStepSelection = (pathwayId, stepId, e) => {
    if (e) e.stopPropagation();
    const key = `${pathwayId}_${stepId}`;
    updateDataCollection({
      selectedSteps: {
        ...selectedSteps,
        [key]: !selectedSteps[key]
      }
    });
  };

  const getPathwayProgress = (pathwayId) => {
    const steps = getStepsForPathway(pathwayId);
    const viewed = steps.filter(step => isStepViewed(pathwayId, step.id)).length;
    return { viewed, total: steps.length };
  };

  const getPathwaySelectedCount = (pathwayId) => {
    const steps = getStepsForPathway(pathwayId);
    return steps.filter(step => isStepSelected(pathwayId, step.id)).length;
  };

  const hasAnySelections = () => {
    return Object.values(selectedSteps).some(v => v === true);
  };

  const handlePathwayClick = (pathway) => {
    setSelectedPathway(pathway);
  };

  const handleStepClick = (step) => {
    setSelectedStep(step);
    setShowStepModal(true);
    markStepViewed(selectedPathway.id, step.id);
  };

  const handleBackToPathways = () => {
    setSelectedPathway(null);
  };

  const handleCloseModal = () => {
    setShowStepModal(false);
    setSelectedStep(null);
  };

  const handleCompletionCheckbox = () => {
    const newValue = !completionConfirmed;
    updateDataCollection({ completionConfirmed: newValue });
    setDataCollectionComplete(newValue);
  };

  const handleNextWizard = () => {
    switchWizard('E');
  };


  const renderPathwayCards = () => (
    <div className="data-collection-landing">
      <div className="dc-header">
        <h2>Data Collection Processes</h2>
        <p>Select a data collection method below to explore detailed guidance and best practices. Click any pathway card to view its steps, then click a step for tips and recommendations.</p>
        {relevantPathways.size > 0 && (
          <div className="dc-context-banner">
            <Lightbulb size={18} />
            <span>Based on your Wizard A selections, we recommend exploring: <strong>{Array.from(relevantPathways).map(id => pathways.find(p => p.id === id)?.title).join(', ')}</strong></span>
          </div>
        )}
      </div>

      <div className="dc-pathways-grid">
        {pathways.map(pathway => {
          const IconComponent = iconMap[pathway.icon] || FileText;
          const isRelevant = relevantPathways.has(pathway.id);
          const progress = getPathwayProgress(pathway.id);

          return (
            <div
              key={pathway.id}
              className={`dc-pathway-card ${isRelevant ? 'relevant' : ''}`}
              onClick={() => handlePathwayClick(pathway)}
            >
              <div className="dc-pathway-header">
                <div className={`dc-pathway-icon ${isRelevant ? 'highlighted' : ''}`}>
                  <IconComponent size={24} />
                </div>
                <span className="dc-pathway-id">Path {pathway.id}</span>
                {isRelevant && <span className="dc-relevant-badge">Recommended</span>}
              </div>
              <h3>{pathway.title}</h3>
              <p>{pathway.description}</p>
              <div className="dc-pathway-footer">
                <span className="dc-step-count">{Object.keys(pathway.steps).length} steps</span>
                {progress.viewed > 0 && (
                  <span className="dc-progress">
                    <CheckCircle size={14} />
                    {progress.viewed}/{progress.total} viewed
                  </span>
                )}
                <ChevronRight size={18} className="dc-arrow" />
              </div>
            </div>
          );
        })}
      </div>

      {hasAnySelections() && (
        <div className="dc-completion-section">
          <label 
            className={`dc-completion-checkbox ${completionConfirmed ? 'checked' : ''}`}
            onClick={handleCompletionCheckbox}
          >
            {completionConfirmed ? (
              <CheckSquare size={24} className="dc-checkbox-icon checked" />
            ) : (
              <Square size={24} className="dc-checkbox-icon" />
            )}
            <span>I have completed the selection of my data collection options and ready to move on</span>
          </label>
          
          {completionConfirmed && (
            <button className="dc-next-wizard-btn" onClick={handleNextWizard}>
              Next Wizard (E)
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderStepsList = () => {
    if (!selectedPathway) return null;
    const steps = getStepsForPathway(selectedPathway.id);
    const IconComponent = iconMap[selectedPathway.icon] || FileText;

    return (
      <div className="dc-steps-view">
        <button className="dc-back-btn" onClick={handleBackToPathways}>
          <ArrowLeft size={18} />
          Back to All Pathways
        </button>

        <div className="dc-pathway-header-full">
          <div className={`dc-pathway-icon-large`}>
            <IconComponent size={32} />
          </div>
          <div>
            <h2>Path {selectedPathway.id}: {selectedPathway.title}</h2>
            <p>{selectedPathway.description}</p>
          </div>
        </div>

        <div className="dc-steps-grid">
          {steps.map(step => {
            const viewed = isStepViewed(selectedPathway.id, step.id);
            const selected = isStepSelected(selectedPathway.id, step.id);
            return (
              <div
                key={step.id}
                className={`dc-step-card ${viewed ? 'viewed' : ''} ${selected ? 'selected' : ''}`}
                onClick={() => handleStepClick(step)}
              >
                <div className="dc-step-header">
                  <span className="dc-step-id">{step.id}</span>
                  <div 
                    className="dc-step-select-toggle"
                    onClick={(e) => toggleStepSelection(selectedPathway.id, step.id, e)}
                  >
                    {selected ? (
                      <CheckSquare size={20} className="dc-select-icon selected" />
                    ) : (
                      <Square size={20} className="dc-select-icon" />
                    )}
                  </div>
                </div>
                <h4>{step.title}</h4>
                <p className="dc-step-intro">{step.introduction.substring(0, 100)}...</p>
                <div className="dc-step-footer">
                  <span className="dc-tip-count">{step.tips.length} tips</span>
                  <ChevronRight size={16} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStepModal = () => {
    if (!showStepModal || !selectedStep) return null;
    const selected = isStepSelected(selectedPathway.id, selectedStep.id);

    return (
      <div className="dc-modal-overlay" onClick={handleCloseModal}>
        <div className="dc-modal" onClick={e => e.stopPropagation()}>
          <div className="dc-modal-header">
            <div>
              <span className="dc-modal-step-id">{selectedStep.id}</span>
              <h2>{selectedStep.title}</h2>
            </div>
            <button className="dc-modal-close" onClick={handleCloseModal}>
              <X size={24} />
            </button>
          </div>

          <div className="dc-modal-selection">
            <label 
              className={`dc-modal-select-checkbox ${selected ? 'checked' : ''}`}
              onClick={() => toggleStepSelection(selectedPathway.id, selectedStep.id)}
            >
              {selected ? (
                <CheckSquare size={22} className="dc-checkbox-icon checked" />
              ) : (
                <Square size={22} className="dc-checkbox-icon" />
              )}
              <span>I select this data collection process</span>
            </label>
          </div>

          <div className="dc-modal-content">
            <div className="dc-modal-intro">
              <p>{selectedStep.introduction}</p>
            </div>

            <div className="dc-tips-list">
              <h3>Tips & Best Practices</h3>
              {selectedStep.tips.map((tip, index) => (
                <div key={index} className="dc-tip-item">
                  <div className="dc-tip-number">{index + 1}</div>
                  <div className="dc-tip-content">
                    <h4>{tip.title}</h4>
                    <p>{tip.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="data-collection-wizard">
      <div className="wizard-d-header">
        <span className="wizard-d-title">Wizard D: Data Collection Processes</span>
      </div>
      {selectedPathway ? renderStepsList() : renderPathwayCards()}
      {renderStepModal()}
    </div>
  );
};

export default DataCollectionWizard;
